/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The client's half of every JSON-LD node in <head>: one script element per
 * id, looked up by that id, created when the page has none yet, its text
 * replaced in place. The bake (scripts/prerender.mjs) and the request-time
 * head (server/seoMeta.ts) write the same nodes under the same ids, so a page
 * that hydrates takes the baked node over instead of adding a second one.
 *
 * One home since 2026-10-14: the pages and hooks that keep a node in the head
 * (useSEO, ArticlePage, BegripPage, the two laadpalen pages, ErePricePage,
 * MidCheckPage, useFAQSchema, useDefinedTermSchema; AuthorPage only drops
 * one) each carried their own copy of these steps.
 *
 * Client-only: call it from an effect or a handler, never at render — the
 * prerender has no `document`. The body needs no escaping here: `textContent`
 * is never parsed as markup (the HTML writers go through jsonLdScriptBody in
 * src/lib/articleSeo.js instead).
 */

/**
 * Write `payload` as the JSON-LD node under `id`, whatever it is — the custom
 * schema an editor typed is written as parsed, `null` included.
 */
export function setJsonLd(id: string, payload: unknown): void {
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement('script');
    el.id = id;
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(payload);
}

/** Drop the JSON-LD node under `id` — the baked one or ours. */
export function removeJsonLd(id: string): void {
  document.getElementById(id)?.remove();
}

/**
 * Write the node a schema builder returned, or drop it when the builder had
 * nothing to say (its `null`): a page never keeps a stale node, and never
 * publishes an empty one.
 */
export function upsertJsonLd(id: string, payload: object | null | undefined): void {
  if (!payload) {
    removeJsonLd(id);
    return;
  }
  setJsonLd(id, payload);
}
